import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Tooltip } from "primereact/tooltip";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSubmissionAnalytics } from "../../../../api/routes";
import { SubmissionAnalyticsRow } from "../../../../client";
import { formatAbbreviatedMoney } from "../../Money";
import {
  statusGroup,
  dateWindowStart,
  DateWindow,
  TIV_BUCKETS,
  LOCATION_BUCKETS,
  sic2Label,
  constructionLabel,
  formatPct,
  topNWithOther,
  HistogramBucket,
} from "./helpers";
import { StatusFlow } from "./StatusFlow";
import styles from "./SubmissionAnalytics.module.css";

const useReportsForAnalytics = () =>
  useQuery({
    queryKey: ["submission-analytics"],
    queryFn: () => getSubmissionAnalytics(),
    refetchInterval: 300_000,
  });

const DATE_WINDOW_OPTIONS: { label: string; value: DateWindow }[] = [
  { label: "All time", value: "all" },
  { label: "YTD", value: "ytd" },
  { label: "Past 12 mo", value: "12mo" },
  { label: "Past 90d", value: "90d" },
  { label: "Past 30d", value: "30d" },
];

type MonthlyBucket = {
  month: string;
  submitted: { count: number; tiv: number; premium: number };
  cleared: { count: number; tiv: number; premium: number };
  quoted: { count: number; tiv: number; premium: number };
  bound: { count: number; tiv: number; premium: number };
};

type MonthlyMetric = "count" | "tiv" | "premium";

const METRIC_OPTIONS: { label: string; value: MonthlyMetric }[] = [
  { label: "Count", value: "count" },
  { label: "TIV", value: "tiv" },
  { label: "Premium", value: "premium" },
];

const formatMonthlyValue = (n: number, metric: MonthlyMetric): string => {
  if (metric === "count") return String(n);
  return formatAbbreviatedMoney(n);
};

const MonthlyTable = ({
  series,
  metric,
}: {
  series: MonthlyBucket[];
  metric: MonthlyMetric;
}) => {
  const valueOf = (b: MonthlyBucket["submitted"]) => b[metric];
  // Show newest months first
  const ordered = [...series].reverse();
  // Columns annotate their premium source when the displayed metric is
  // premium: submitted / cleared use the broker target (defined for every
  // layer), while quoted / bound use the PolicyFly all-in total. Count and
  // TIV keep clean headers since the source distinction doesn't apply.
  const targetSuffix = metric === "premium" ? " (target)" : "";
  const policyFlySuffix = metric === "premium" ? " (PolicyFly)" : "";
  return (
    <table className={styles.segTable}>
      {/* Fixed column widths so switching between Count / TIV / Premium
          doesn't reflow the table — the Month column is narrow, the five
          data columns split the rest equally. */}
      <colgroup>
        <col className={styles.colMonth} />
        <col />
        <col />
        <col />
        <col />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th>Month</th>
          <th className={styles.numCol}>Submitted{targetSuffix}</th>
          <th className={styles.numCol}>Cleared{targetSuffix}</th>
          <th className={styles.numCol}>Quoted{policyFlySuffix}</th>
          <th className={styles.numCol}>Bound{policyFlySuffix}</th>
          <th className={styles.numCol}>Sub→Bind</th>
        </tr>
      </thead>
      <tbody>
        {ordered.map((s) => {
          const submittedV = valueOf(s.submitted);
          const clearedV = valueOf(s.cleared);
          const quotedV = valueOf(s.quoted);
          const boundV = valueOf(s.bound);
          const sb = submittedV > 0 ? (boundV / submittedV) * 100 : 0;
          return (
            <tr key={s.month}>
              <td>{s.month}</td>
              <td className={styles.numCol}>
                {formatMonthlyValue(submittedV, metric)}
              </td>
              <td className={styles.numCol}>
                {formatMonthlyValue(clearedV, metric)}
              </td>
              <td className={styles.numCol}>
                {formatMonthlyValue(quotedV, metric)}
              </td>
              <td className={styles.numCol}>
                {formatMonthlyValue(boundV, metric)}
              </td>
              <td className={styles.numCol}>
                {submittedV > 0 ? formatPct(sb) : "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

type HistogramAxis = "tiv" | "locations";

const HISTOGRAM_AXIS_OPTIONS: { label: string; value: HistogramAxis }[] = [
  { label: "By TIV", value: "tiv" },
  { label: "By locations", value: "locations" },
];

const buildHistogram = (
  reports: SubmissionAnalyticsRow[],
  axis: HistogramAxis
): { label: string; count: number; bound: number }[] => {
  const buckets = (axis === "tiv" ? TIV_BUCKETS : LOCATION_BUCKETS).map(
    (b) => ({ label: b.label, count: 0, bound: 0 })
  );
  for (const r of reports) {
    const tiv = r.total_tiv ?? 0;
    const numLocs = r.num_locations ?? 0;
    // TIV uses right-exclusive ranges (`>= min && < max`); the top bucket's
    // max is Infinity so anything finite lands somewhere. Locations use
    // both-inclusive ranges since each bucket is an integer count.
    const idx =
      axis === "tiv"
        ? TIV_BUCKETS.findIndex((b) => tiv >= b.min && tiv < b.max)
        : LOCATION_BUCKETS.findIndex(
            (b) => numLocs >= b.min && numLocs <= b.max
          );
    const b = idx >= 0 ? buckets[idx] : undefined;
    if (b) {
      b.count += 1;
      if (statusGroup(r.status) === "bound") b.bound += 1;
    }
  }
  return buckets;
};

const HistogramBars = ({ buckets }: { buckets: HistogramBucket[] }) => {
  const max = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <div className={styles.histogramRows}>
      <div className={`${styles.histRow} ${styles.histHeader}`}>
        <div />
        <div />
        <div className={styles.histValue}>Subs</div>
        <div className={styles.histValue}>Bound</div>
        <div className={styles.histBindRate}>Sub→Bind</div>
      </div>
      {buckets.map((b) => {
        const pct = (b.count / max) * 100;
        const bindRate = b.count > 0 ? (b.bound / b.count) * 100 : 0;
        return (
          <div key={b.label} className={styles.histRow}>
            <div className={styles.histLabel} data-pr-tooltip={b.label}>
              {b.label}
            </div>
            <div className={styles.histBarTrack}>
              <div
                className={styles.histBarFill}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className={styles.histValue}>{b.count}</div>
            <div className={styles.histValue}>{b.bound}</div>
            <div className={styles.histBindRate}>
              {b.count > 0 ? formatPct(bindRate) : "—"}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const DealSizeHistogram = ({
  reports,
  axis,
}: {
  reports: SubmissionAnalyticsRow[];
  axis: HistogramAxis;
}) => {
  const buckets = useMemo(() => buildHistogram(reports, axis), [reports, axis]);
  return <HistogramBars buckets={buckets} />;
};

// Bucket each report by a string key derived from a row field, sum bound
// counts per bucket, sort descending by count, then collapse the long tail
// into "Other" so the half-width grid charts stay legible.
const HISTOGRAM_TOP_N = 10;

const buildKeyedHistogram = (
  reports: SubmissionAnalyticsRow[],
  keyFor: (r: SubmissionAnalyticsRow) => { key: string; label: string }
): HistogramBucket[] => {
  const map = new Map<string, HistogramBucket>();
  for (const r of reports) {
    const { key, label } = keyFor(r);
    let b = map.get(key);
    if (!b) {
      b = { label, count: 0, bound: 0 };
      map.set(key, b);
    }
    b.count += 1;
    if (statusGroup(r.status) === "bound") b.bound += 1;
  }
  const sorted = Array.from(map.values()).sort((a, b) => b.count - a.count);
  return topNWithOther(sorted, HISTOGRAM_TOP_N);
};

const SicHistogram = ({ reports }: { reports: SubmissionAnalyticsRow[] }) => {
  const buckets = useMemo(
    () =>
      buildKeyedHistogram(reports, (r) => {
        const code = r.mode_sic_prefix ?? "";
        const sic2 = code && code.length >= 2 ? code.slice(0, 2) : "Unknown";
        return {
          key: sic2,
          label: sic2 === "Unknown" ? "Unknown" : sic2Label(sic2),
        };
      }),
    [reports]
  );
  return <HistogramBars buckets={buckets} />;
};

const BrokerHistogram = ({
  reports,
}: {
  reports: SubmissionAnalyticsRow[];
}) => {
  const buckets = useMemo(
    () =>
      buildKeyedHistogram(reports, (r) => {
        const broker =
          r.broker_organization_name || r.broker_name || "Unknown broker";
        return { key: broker, label: broker };
      }),
    [reports]
  );
  return <HistogramBars buckets={buckets} />;
};

const ConstructionHistogram = ({
  reports,
}: {
  reports: SubmissionAnalyticsRow[];
}) => {
  const buckets = useMemo(
    () =>
      buildKeyedHistogram(reports, (r) => {
        const code = r.mode_construction_code ?? "";
        return code
          ? { key: code, label: constructionLabel(code) }
          : { key: "Unknown", label: "Unknown" };
      }),
    [reports]
  );
  return <HistogramBars buckets={buckets} />;
};

export const SubmissionAnalytics = () => {
  const reportsQ = useReportsForAnalytics();

  const [window, setWindow] = useState<DateWindow>("30d");
  const [monthlyMetric, setMonthlyMetric] = useState<MonthlyMetric>("count");
  const [histogramAxis, setHistogramAxis] = useState<HistogramAxis>("tiv");

  const allReports = reportsQ.data?.reports ?? [];
  const lastRefreshedAt = reportsQ.data?.last_refreshed_at
    ? new Date(reportsQ.data.last_refreshed_at).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;
  const reports = useMemo(() => {
    const start = dateWindowStart(window);
    if (!start) return allReports;
    const startMs = start.getTime();
    return allReports.filter(
      (r) => new Date(r.created_at).getTime() >= startMs
    );
  }, [allReports, window]);

  const monthlySeries = useMemo<MonthlyBucket[]>(() => {
    const empty = (month: string): MonthlyBucket => ({
      month,
      submitted: { count: 0, tiv: 0, premium: 0 },
      cleared: { count: 0, tiv: 0, premium: 0 },
      quoted: { count: 0, tiv: 0, premium: 0 },
      bound: { count: 0, tiv: 0, premium: 0 },
    });
    const buckets = new Map<string, MonthlyBucket>();
    for (const r of reports) {
      const d = new Date(r.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      let b = buckets.get(key);
      if (!b) {
        b = empty(key);
        buckets.set(key, b);
      }
      const g = statusGroup(r.status);
      const tiv = r.total_tiv ?? 0;
      // Pre-quote stages don't have PolicyFly data yet, so submitted/cleared
      // premiums use the broker ask. Quoted/bound stages use the PolicyFly
      // actual. Column headers in MonthlyTable label the first two with
      // "(target)" to make this split visible.
      const targetPrem = r.sum_layer_target_premium ?? 0;
      const totalPrem = r.sum_policyfly_total_premium ?? 0;
      b.submitted.count += 1;
      b.submitted.tiv += tiv;
      b.submitted.premium += targetPrem;
      // Cleared = anything that made it past clearance (cleared / in_progress
      // / indicated / quoted / bound). Excludes not_cleared / lost / declined.
      if (
        g === "cleared" ||
        g === "in_progress" ||
        g === "indicated" ||
        g === "quoted" ||
        g === "bound"
      ) {
        b.cleared.count += 1;
        b.cleared.tiv += tiv;
        b.cleared.premium += targetPrem;
      }
      if (g === "quoted" || g === "bound") {
        b.quoted.count += 1;
        b.quoted.tiv += tiv;
        b.quoted.premium += totalPrem;
      }
      if (g === "bound") {
        b.bound.count += 1;
        b.bound.tiv += tiv;
        b.bound.premium += totalPrem;
      }
    }
    const sorted = Array.from(buckets.values()).sort((a, b) =>
      a.month.localeCompare(b.month)
    );
    return sorted.slice(-18);
  }, [reports]);

  if (!reportsQ.data) {
    return (
      <div className={`${styles.container} ${styles.loading}`}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* One Tooltip instance for every histogram label on the page — the
          ellipsis-truncated rows in the 2x2 grid rely on hover to reveal
          the full broker / SIC / construction text. */}
      <Tooltip target={`.${styles.histLabel}`} showDelay={500} />
      <div className={styles.headerRow}>
        <div className={styles.headerTop}>
          <div className={styles.title}>Submission Analytics</div>
          <SelectButton
            value={window}
            onChange={(e) => e.value && setWindow(e.value as DateWindow)}
            options={DATE_WINDOW_OPTIONS}
            optionLabel="label"
            optionValue="value"
            unselectable={false}
          />
        </div>
        <div className={styles.subTitle}>
          {lastRefreshedAt ? `Refreshed ${lastRefreshedAt} · ` : ""}
          {reports.length} submissions in window
          {allReports.length !== reports.length
            ? ` (${allReports.length} total)`
            : ""}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Status flow</div>
        <div className={styles.subSubtle}>
          Where the {reports.length} submissions in this window stand today.
          Each is counted once, placed at the furthest stage it reached; the
          spine tracks how far the cohort advanced through clearance,
          underwriting, quote, and bind, shedding still-open and exited
          submissions at each stage.
        </div>
        <StatusFlow reports={reports} />
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitleRow}>
          <div className={styles.sectionTitle}>Submissions by month</div>
          <SelectButton
            value={monthlyMetric}
            onChange={(e) =>
              e.value && setMonthlyMetric(e.value as MonthlyMetric)
            }
            options={METRIC_OPTIONS}
            optionLabel="label"
            optionValue="value"
            unselectable={false}
          />
        </div>
        {monthlySeries.length === 0 ? (
          <div className={styles.subSubtle}>
            No submissions in the selected window.
          </div>
        ) : (
          <MonthlyTable series={monthlySeries} metric={monthlyMetric} />
        )}
      </div>

      <div className={styles.histogramGrid}>
        <div className={styles.section}>
          <div className={styles.sectionTitleRow}>
            <div className={styles.sectionTitle}>Deal size distribution</div>
            <SelectButton
              value={histogramAxis}
              onChange={(e) =>
                e.value && setHistogramAxis(e.value as HistogramAxis)
              }
              options={HISTOGRAM_AXIS_OPTIONS}
              optionLabel="label"
              optionValue="value"
              unselectable={false}
            />
          </div>
          <div className={styles.subSubtle}>
            Count of submissions bucketed by{" "}
            {histogramAxis === "tiv" ? "total insured value" : "location count"}
            .
          </div>
          {reports.length === 0 ? (
            <div className={styles.subSubtle}>
              No submissions in the selected window.
            </div>
          ) : (
            <DealSizeHistogram reports={reports} axis={histogramAxis} />
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            Submissions by SIC (2-digit)
          </div>
          <div className={styles.subSubtle}>
            Modal 2-digit SIC across each report's addresses.
          </div>
          {reports.length === 0 ? (
            <div className={styles.subSubtle}>
              No submissions in the selected window.
            </div>
          ) : (
            <SicHistogram reports={reports} />
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Submissions by broker</div>
          <div className={styles.subSubtle}>Broker organization.</div>
          {reports.length === 0 ? (
            <div className={styles.subSubtle}>
              No submissions in the selected window.
            </div>
          ) : (
            <BrokerHistogram reports={reports} />
          )}
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>Submissions by construction</div>
          <div className={styles.subSubtle}>
            TIV-weighted modal construction code across each report's addresses.
          </div>
          {reports.length === 0 ? (
            <div className={styles.subSubtle}>
              No submissions in the selected window.
            </div>
          ) : (
            <ConstructionHistogram reports={reports} />
          )}
        </div>
      </div>
    </div>
  );
};
